<template>
  <SafeTeleport to="body">
    <Drawer
      ref="drawer"
      placement="bottom"
      :title="TITLE"
      :aria-label="TITLE"
      :no-footer="!footerVisible"
      data-cy="technician-picker-sheet"
      @show-drawer="focusFilter"
      @hide-drawer="emit('close')"
    >
      <!-- Replaces the drawer's default heading block so the search field and
           the chip row ride in the panel's non-scrolling first grid row. Put in
           the body instead they would scroll away from a searching admin. -->
      <template #title>
        <div class="mb-4 flex flex-col gap-3 pr-8">
          <h5 class="text-base font-semibold text-gray-900">{{ TITLE }}</h5>

          <SelectInputFilter
            ref="filter"
            v-model="query"
            data-cy="technician-search"
            placeholder="Search technicians"
          />

          <div
            class="-mx-4 flex gap-2 overflow-x-auto whitespace-nowrap px-4 pb-1"
            data-cy="department-chips"
          >
            <button
              v-for="chip in chips"
              :key="chip.value"
              type="button"
              :class="[
                'shrink-0 rounded-full border px-3 py-1 text-sm',
                chip.value === departmentId
                  ? 'border-primary-600 bg-primary-50 text-primary-700'
                  : 'border-gray-300 bg-white text-gray-700'
              ]"
              :data-cy="`department-chip-${chip.value}`"
              @click="selectDepartment(chip.value)"
            >
              {{ chip.label }}
            </button>
          </div>

          <button
            type="button"
            class="self-start text-sm font-medium text-primary-700"
            data-cy="select-all"
            @click="toggleSelectAll"
          >
            {{ allListedSelected ? "Deselect all" : "Select all" }}
          </button>
        </div>
      </template>

      <!-- A height floor, not a preference. The drawer is anchored to the bottom
           edge and sizes to its content, so without one the panel shrinks as the
           list filters and everything above it slides DOWN: measured on a 844px
           screen, the search field being typed into travelled 300px between an
           empty query and a one-result one, and back up again on the jump from
           one result to none. `70dvh` is the drawer's own max height, so the
           panel simply sits at its cap for every query and the list scrolls
           inside it -- stable regardless of how tall the header above grows. -->
      <div class="technician-list pb-2">
        <TechnicianSelectRow
          v-if="unassignedRow"
          :resource="unassignedRow"
          :selected="isSelected(unassignedRow.id)"
          :unassigned-count="unassignedCount"
          @toggle="toggle"
        />

        <template v-if="isSearching">
          <TechnicianSelectRow
            v-for="technician in matches"
            :key="technician.id"
            :resource="technician"
            :selected="isSelected(technician.id)"
            @toggle="toggle"
          />
        </template>

        <template v-else>
          <div v-for="group in groups" :key="group.key">
            <h6
              class="bg-gray-50 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-gray-500"
              data-cy="department-heading"
            >
              {{ group.label }}
            </h6>
            <TechnicianSelectRow
              v-for="technician in group.technicians"
              :key="technician.id"
              :resource="technician"
              :selected="isSelected(technician.id)"
              @toggle="toggle"
            />
          </div>
        </template>

        <p
          v-if="isEmpty"
          class="px-4 py-8 text-center text-sm text-gray-500"
          data-cy="technician-empty-state"
        >
          No technicians match that search.
        </p>
      </div>

      <template #footer>
        <div class="flex w-full items-center justify-between gap-3">
          <div class="flex min-w-0 flex-col">
            <span
              class="truncate text-sm text-gray-900"
              data-cy="selected-summary"
            >
              {{ summary }}
            </span>
            <button
              type="button"
              class="self-start text-sm font-medium text-primary-700"
              data-cy="clear-selection"
              @click="clear"
            >
              Clear
            </button>
          </div>
          <SeraButton
            :label="`Show ${draft.length}`"
            :disabled="draft.length === 0"
            data-cy="apply-selection"
            @click="apply"
          />
        </div>
      </template>
    </Drawer>
  </SafeTeleport>
</template>

<script setup lang="ts">
import { computed, nextTick, onMounted, ref, watch } from "vue";
import { SafeTeleport } from "vue-safe-teleport";
import { trackMobileWebEvent } from "@/lib/pendo";

import SeraButton from "@/components/Seranova/Buttons/Button.vue";
import Drawer from "@/components/Seranova/Drawer/Drawer.vue";
import SelectInputFilter from "@/components/Seranova/SelectInput/SelectInputFilter.vue";
import TechnicianSelectRow from "@/SchedulerV2/TechnicianSelectRow.vue";
import {
  ALL_DEPARTMENTS,
  type DepartmentOption,
  type SelectableResource,
  useTechnicianSelection
} from "@/SchedulerV2/useTechnicianSelection";
import { UNASSIGNED_RESOURCE_ID } from "@/typings/scheduler";

/**
 * The technician chooser for the phone scheduler.
 *
 * TB-2250 opened this file as a single-select list of `ResourceCard`s on
 * `BottomSheet`, with a comment saying this story would add search, department
 * chips and multi-select "on top of this". It grew into those rather than
 * gaining a second sheet beside it, which is why the file keeps its name, its
 * `technician-picker-sheet` hook and its mount in `SchedulePageV2`.
 *
 * It moved to `Drawer placement="bottom"` on the way. That is the placement this
 * ticket's technical notes specify and the one the rest of the epic ships
 * (TB-2209 added it; TB-2251's appointment sheet and `AppointmentCreateSurface`
 * use it), and `BottomSheet` has no footer slot for the "N selected · Show N"
 * bar this story's AC 8 requires.
 *
 * Purely presentational: the roster, the departments and the incoming selection
 * all arrive as props, and the only outputs are `apply` and `close`. That is
 * deliberate rather than tidiness — `useEventCalendar` is a provideHook, and a
 * hook called from a teleported subtree that misses the provider silently
 * constructs a SECOND instance whose calendar is null and whose resources are
 * empty. Taking data as props makes that impossible, and lets the control row
 * in TB-2250 mount this without rework.
 */
const TITLE = "Choose a technician";

const props = withDefaults(
  defineProps<{
    resources: SelectableResource[];
    departments: DepartmentOption[];
    selectedIds?: number[];
    unassignedCount?: number;
  }>(),
  { selectedIds: () => [], unassignedCount: 0 }
);

const emit = defineEmits<{
  (e: "apply", ids: number[]): void;
  (e: "close"): void;
}>();

const drawer = ref<InstanceType<typeof Drawer> | null>(null);
const filter = ref<InstanceType<typeof SelectInputFilter> | null>(null);
const query = ref("");
const departmentId = ref<number | typeof ALL_DEPARTMENTS>(ALL_DEPARTMENTS);
const draft = ref<number[]>([...props.selectedIds]);

const { unassignedRow, groups, matches, isSearching, isEmpty, listedIds } =
  useTechnicianSelection({
    resources: () => props.resources,
    departments: () => props.departments,
    query,
    departmentId
  });

interface Chip {
  value: number | typeof ALL_DEPARTMENTS;
  label: string;
}

const chips = computed<Chip[]>(() => [
  { value: ALL_DEPARTMENTS, label: "All departments" },
  ...props.departments.map((department) => ({
    value: department.id,
    label: department.name
  }))
]);

const draftIds = computed(() => new Set(draft.value));
const focusFilter = () => {
  nextTick(() => filter.value?.focus());
};

const isSelected = (id: number) => draftIds.value.has(id);

const allListedSelected = computed(
  () =>
    listedIds.value.length > 0 && listedIds.value.every((id) => isSelected(id))
);

/**
 * The footer is keyed on the selection having *changed*, not on it being
 * non-empty: reopening the sheet on an existing selection must not present a
 * pending-looking Show button before the admin has touched anything.
 */
const footerVisible = computed(() => {
  const incoming = new Set(props.selectedIds);
  return (
    draft.value.length !== incoming.size ||
    draft.value.some((id) => !incoming.has(id))
  );
});

/**
 * "shown side by side" describes what Show is about to do, so it has no
 * business sitting next to a Show that cannot be pressed. Unchecking every row
 * reaches this state: the footer stays (collapsing it would pull the control
 * out from under the finger that just unchecked the last one) with nothing
 * left to apply.
 */
const summary = computed(() =>
  draft.value.length
    ? `${draft.value.length} selected · shown side by side`
    : "0 selected"
);

const toggle = (id: number) => {
  draft.value = isSelected(id)
    ? draft.value.filter((selected) => selected !== id)
    : [...draft.value, id];
};

// Scoped to what is listed, so an active chip or search term never reaches a row
// the admin cannot see. A chip lists Unassigned rather than hiding it, so this
// does reach it there; a search term that does not match its name still hides
// it, and its selected state is then left alone.
const toggleSelectAll = () => {
  const listed = listedIds.value;
  draft.value = allListedSelected.value
    ? draft.value.filter((id) => !listed.includes(id))
    : [...new Set([...draft.value, ...listed])];
};

/**
 * Clear resets to what the phone board shows by default — the Unassigned
 * column — rather than to nothing, so Show stays pressable and applying it is
 * how a saved selection is cleared. Keyed on the roster rather than on the
 * listed row: a search term hiding Unassigned does not stop it being the
 * default.
 */
const clear = () => {
  const hasUnassigned = props.resources.some(
    (resource) => resource.id === UNASSIGNED_RESOURCE_ID
  );
  draft.value = hasUnassigned ? [UNASSIGNED_RESOURCE_ID] : [];
};

const selectDepartment = (value: number | typeof ALL_DEPARTMENTS) => {
  departmentId.value = value;
  trackMobileWebEvent("scheduler_technician_sheet_department_chip", {
    department_id: value
  });
};

const apply = () => {
  trackMobileWebEvent("scheduler_technician_sheet_apply", {
    technician_count: draft.value.length
  });
  emit("apply", [...draft.value]);
  drawer.value?.close();
};

// Once per opening: the point is how often search is reached for, not keystrokes.
const searchTracked = ref(false);
watch(query, (value) => {
  if (!value.trim() || searchTracked.value) return;
  searchTracked.value = true;
  trackMobileWebEvent("scheduler_technician_sheet_search");
});

onMounted(() => {
  trackMobileWebEvent("scheduler_technician_sheet_open", {
    technician_count: props.resources.length
  });
});
</script>

<style scoped lang="scss">
/* The height floor described on the list element above. It is a declaration
   pair rather than a Tailwind utility for the same reason as the drawer's own
   cap: the `vh` value has to come first so a browser without `dvh` still gets a
   floor, and Tailwind does not guarantee the order of two arbitrary min-height
   utilities in its output. Matching the drawer's 70 keeps the panel pinned at
   its cap for every query. */
.technician-list {
  min-height: 70vh;
  min-height: 70dvh;
}
</style>
